import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import PasswordField from "@/components/PasswordField";
import PasswordStrength from "@/components/PasswordStrength";
import { KeyRound, AtSign, ShieldCheck, ChevronDown, MailCheck } from "@/lib/icons";
import EmailDeliveryNotice, { withEmailDeliveryNote } from "@/components/EmailDeliveryNotice";

/**
 * Renders "Change password" and "Change email" collapsible sections
 * on the member account page. All auth logic runs server-side.
 */
export default function AccountSecurity({ user, onUserUpdated }) {
  return (
    <div className="mt-8 grid gap-6 md:grid-cols-2">
      <ChangePasswordSection />
      <ChangeEmailSection user={user} onUserUpdated={onUserUpdated} />
    </div>
  );
}

function Card({ children, testid }) {
  return (
    <div className="glass rounded-3xl p-6 md:p-7 fade-up-slow" data-testid={testid}>
      {children}
    </div>
  );
}

function ChangePasswordSection() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (next !== confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/member/password/change", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm(""); setOpen(false);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card testid="account-change-password">
      <button type="button" onClick={() => setOpen((x) => !x)}
        className="w-full flex items-center justify-between text-left" data-testid="acc-cp-toggle">
        <div>
          <div className="overline text-fuchsia-300 flex items-center gap-2"><KeyRound size={12} /> Change password</div>
          <div className="mt-1 text-white font-semibold">Update your account password</div>
        </div>
        <ChevronDown size={16} className={`text-white/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <form onSubmit={submit} className="mt-5 space-y-3">
          <PwField label="Current password" value={current} onChange={setCurrent} testid="acc-cp-current" autoComplete="current-password" />
          <PwField label="New password" value={next} onChange={setNext} testid="acc-cp-new" autoComplete="new-password" showStrength />
          <PwField label="Confirm new password" value={confirm} onChange={setConfirm} testid="acc-cp-confirm" autoComplete="new-password" />
          {err && <Err msg={err} />}
          <button type="submit" disabled={busy} data-testid="acc-cp-submit"
            className="btn-glow rounded-full px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60">
            <ShieldCheck size={13} /> {busy ? "Saving…" : "Update password"}
          </button>
        </form>
      )}
    </Card>
  );
}

function ChangeEmailSection({ user, onUserUpdated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);          // 1: pw + new email, 2: OTP
  const [current, setCurrent] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const start = async (e) => {
    e.preventDefault();
    setErr("");
    if (newEmail !== confirmEmail) { setErr("New email addresses do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/member/email/change/start", {
        current_password: current, new_email: newEmail.trim().toLowerCase(),
      });
      setOtpToken(data.otp_token);
      setStep(2); setCooldown(60);
      toast.success(withEmailDeliveryNote(data.message || "Code sent to the new email."));
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/member/email/change/start", {
        current_password: current, new_email: newEmail.trim().toLowerCase(),
      });
      setOtpToken(data.otp_token);
      setCooldown(60);
      toast.success(withEmailDeliveryNote("Code resent."));
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/member/email/change/verify", { otp_token: otpToken, otp });
      toast.success("Email updated");
      onUserUpdated?.({ ...user, email: data.email });
      // reset
      setCurrent(""); setNewEmail(""); setConfirmEmail(""); setOtp(""); setOtpToken("");
      setStep(1); setOpen(false);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card testid="account-change-email">
      <button type="button" onClick={() => setOpen((x) => !x)}
        className="w-full flex items-center justify-between text-left" data-testid="acc-ce-toggle">
        <div>
          <div className="overline text-fuchsia-300 flex items-center gap-2"><AtSign size={12} /> Change email</div>
          <div className="mt-1 text-white font-semibold">
            Current: <span className="text-white/60 font-normal">{user?.email || "Not set"}</span>
          </div>
        </div>
        <ChevronDown size={16} className={`text-white/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && step === 1 && (
        <form onSubmit={start} className="mt-5 space-y-3">
          <PwField label="Current password" value={current} onChange={setCurrent} testid="acc-ce-current" autoComplete="current-password" />
          <TxtField label="New email address" value={newEmail} onChange={setNewEmail} type="email" testid="acc-ce-new" />
          <TxtField label="Confirm new email address" value={confirmEmail} onChange={setConfirmEmail} type="email" testid="acc-ce-confirm" />
          {err && <Err msg={err} />}
          <button type="submit" disabled={busy} data-testid="acc-ce-start"
            className="btn-glow rounded-full px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60">
            <MailCheck size={13} /> {busy ? "Sending…" : "Send code to new email"}
          </button>
        </form>
      )}

      {open && step === 2 && (
        <form onSubmit={verify} className="mt-5 space-y-3">
          <p className="text-xs text-white/60">We sent a 6-digit code to <span className="text-fuchsia-300">{newEmail}</span>. Expires in 5 minutes.</p>
          <EmailDeliveryNotice />
          <div>
            <label className="overline text-white/60 text-[10px]">Verification code</label>
            <Input required data-testid="acc-ce-otp" maxLength={6} inputMode="numeric" autoComplete="one-time-code"
              className="mt-2 h-12 text-center text-2xl tracking-[0.4em] bg-white/5 border-white/10 text-white"
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          </div>
          {err && <Err msg={err} />}
          <div className="flex items-center gap-3">
            <button type="submit" disabled={busy || otp.length !== 6} data-testid="acc-ce-verify"
              className="btn-glow rounded-full px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60">
              <ShieldCheck size={13} /> {busy ? "Verifying…" : "Verify & update"}
            </button>
            <button type="button" onClick={resend} disabled={cooldown > 0 || busy}
              className="text-xs text-white/50 hover:text-fuchsia-300 disabled:opacity-40" data-testid="acc-ce-resend">
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
            </button>
            <button type="button" onClick={() => setStep(1)}
              className="ml-auto text-xs text-white/40 hover:text-white/70">Back</button>
          </div>
        </form>
      )}
    </Card>
  );
}

function PwField({ label, value, onChange, testid, autoComplete, showStrength }) {
  return (
    <div>
      <label className="overline text-white/60 text-[10px]">{label}</label>
      <div className="mt-1.5">
        <PasswordField value={value} onChange={onChange} testid={testid} autoComplete={autoComplete}
          icon={null} className="h-10 bg-white/5 border-white/10 text-white" />
      </div>
      {showStrength && <PasswordStrength password={value} testid={`${testid}-strength`} />}
    </div>
  );
}
function TxtField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="overline text-white/60 text-[10px]">{label}</label>
      <Input required type={type} data-testid={testid}
        className="mt-1.5 h-10 bg-white/5 border-white/10 text-white"
        value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function Err({ msg }) {
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
      {msg}
    </div>
  );
}
